import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, Inject, Optional, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HeapService } from 'src/app/api-client/heap.service';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import Hotjar from '@hotjar/browser';
import { AuthService } from '../service/auth.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import {
  CreateUserRequest,
  Invitation,
  InvitationsService,
  UsersService,
} from 'ldt-identity-service-api';
import {
  SocialAuthService,
  SocialUser,
  MicrosoftLoginProvider,
} from '@abacritt/angularx-social-login';
import { HostnameService } from 'src/app/hostname-router-service';
import {
  StartLedgerOnboardingRequest,
  WorkforceOnboardingService,
} from 'ldt-onboarding-service-api';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Log } from 'src/app/logger.service';
declare const window: any;

export interface LogInSignUpDialogData {
  isDialog: boolean;
  isPalMode?: boolean;
}

@Component({
  selector: 'app-log-in-sign-up',
  templateUrl: './log-in-sign-up.component.html',
  styleUrls: ['./log-in-sign-up.component.scss'],
  standalone: false,
})
export class LogInSignUpComponent implements OnInit, OnDestroy {
  loginInvalid = false;
  cachedGoogleUser: SocialUser | null = null;
  cachedMicrosoftUser: SocialUser | null = null;
  socialSubscription: Subscription;
  isInvite: boolean = false;
  inviteError: boolean = false;
  inviteErrorMessage: string = '';
  inviteToken: string | undefined;
  inviteType: string = 'personal';
  invitation: Invitation;
  isWorkforceLogin: boolean = false;
  inviteSent: boolean = false;

  isActionLogin = false;
  showPassword: boolean = false;
  showPasswordRequirements = false;
  error: string | undefined = undefined;
  loading: boolean = false;
  isDialog: boolean = false;
  isPalMode: boolean = false;

  signupForm: FormGroup;
  loginForm: FormGroup;
  acceptInviteForm: FormGroup;
  createAccountGoogleForm: FormGroup;
  acceptInviteGoogleForm: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private notify: NotificationService,
    private heapService: HeapService,
    private userService: UsersService,
    private inviteService: InvitationsService,
    private socialAuthService: SocialAuthService,
    private recaptcha: ReCaptchaV3Service,
    private onboardingService: WorkforceOnboardingService,
    @Optional() private dialogRef: MatDialogRef<LogInSignUpComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: LogInSignUpDialogData
  ) {
    // Check if component is opened as a dialog
    if (dialogData) {
      this.isDialog = dialogData.isDialog || false;
      this.isPalMode = dialogData.isPalMode || false;
      // Set default to create account when opened in dialog
      this.isActionLogin = false;
    }

    // Handle redirects from the invite page
    if (window.history.state && window.history.state.inviteToken) {
      this.inviteToken = window.history.state.inviteToken;
      if (window.history.state.type) {
        this.inviteType = window.history.state.type;
      }
    }

    if (this.inviteType === 'moneyball_onboarding') {
      this.authService.setRedirectUrl('/main');
    }

    // Used for password-based signup - ends up creating and sending an invite
    this.signupForm = this.fb.group({
      fullName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      companyName: ['', [Validators.required]],
    });

    // Used for password-based login
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });

    // Used for password-based invite acceptance
    this.acceptInviteForm = this.fb.group({
      fullName: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(64)]],
      confirmPassword: ['', [Validators.required]],
      termsAgreed: [false, [Validators.requiredTrue]],
    });

    // Used for Google-based invite acceptance
    this.acceptInviteGoogleForm = this.fb.group({
      termsAgreed: [false, [Validators.requiredTrue]],
    });

    // Used for Google-based signup - directly signs up without an invite
    this.createAccountGoogleForm = this.fb.group({
      companyName: ['', [Validators.required]],
      termsAgreed: [false, [Validators.requiredTrue]],
    });
  }

  pValidation = [
    {
      func: this.hasLength,
      text: 'Between 8 and 64 characters',
      valid: true,
    },
  ];

  ngOnInit() {
    this.route.data.subscribe((data) => {
      if (data.isLogin) {
        this.isActionLogin = true;
      }
    });

    if (this.inviteToken) {
      this.isActionLogin = false;
      this.inviteService.getInvitationByToken(this.inviteToken).subscribe({
        next: (r: Invitation) => {
          this.invitation = r;
          this.isInvite = true;
          this.acceptInviteForm.patchValue({
            fullName: this.invitation.inviteeName,
          });
        },
        error: (err: any) => {
          this.inviteToken = undefined;
          this.inviteError = true;

          if (err.status === 404) {
            this.inviteErrorMessage =
              'Invalid invitation token provided.\nPlease contact your administrator or support@livedatatechnologies.com for help.';
          } else if (err.status === 409) {
            this.inviteErrorMessage =
              'Invitation has expired.\nPlease ask your administrator to send a new invitation.';
          } else {
            this.inviteErrorMessage =
              'Unknown error occurred.\nPlease contact your administrator or support@livedatatechnologies.com for help.';
          }
        },
      });
    }

    // only set if not already set
    if (!this.socialSubscription) {
      this.socialSubscription = this.socialAuthService.authState.subscribe({
        next: (user) => {
          if (user) {
            if (this.invitation && this.invitation.inviteeEmail !== user.email) {
              this.notify.error(
                'Please use the email address associated with your invitation (' +
                  this.invitation.inviteeEmail +
                  ') to accept this invitation.'
              );
              this.socialAuthService.signOut();
              return;
            }

            // Handle both Google and Microsoft login
            const isGoogleUser = user.provider === 'GOOGLE';
            const isMicrosoftUser = user.provider === 'MICROSOFT';

            const loginObservable = isGoogleUser
              ? this.authService.loginGoogle(user.idToken)
              : this.authService.loginMicrosoft(user.idToken);

            loginObservable.subscribe({
              next: (r) => {
                this.postLogin();
              },
              error: (err) => {
                Log.s('Error logging in with Google or Microsoft', 'error', {
                  user: user.email,
                  status: err.status,
                  message: err.message,
                });
                if (err.status) {
                  switch (err.status) {
                    case 401:
                      this.notify.error(
                        `Error validating user with ${isGoogleUser ? 'Google' : 'Microsoft'}. Please try again later.`
                      );
                      break;
                    case 404:
                      // Cache the user based on provider
                      if (isGoogleUser) {
                        this.cachedGoogleUser = user;
                      } else if (isMicrosoftUser) {
                        this.cachedMicrosoftUser = user;
                      }

                      // New user, either with an invite or not
                      this.isActionLogin = false;

                      break;
                    case 500:
                      this.notify.error(
                        `Sorry, there was a problem logging in with ${isGoogleUser ? 'Google' : 'Microsoft'}. Please try again later.`
                      );
                      break;
                  }
                } else {
                  this.notify.error(
                    `Sorry, there was a problem logging in with ${isGoogleUser ? 'Google' : 'Microsoft'}. Please try again later`
                  );
                }
              },
            });
          }
        },
      });
    }

    this.isWorkforceLogin = HostnameService.isWorkforceHostname();
  }

  ngOnDestroy() {
    if (this.socialSubscription) {
      this.socialSubscription.unsubscribe();
    }
  }

  // createAccountGoogleForm submit action
  createAccountWithGoogle() {
    if (!this.cachedGoogleUser) {
      this.error = 'Oops, something went wrong. Please try again later.';
      this.authService.logOut();
      return;
    }

    if (!this.createAccountGoogleForm.valid) {
      this.error = 'Please fill out all fields to continue';
      return;
    }

    this.loading = true;

    const { companyName } = this.createAccountGoogleForm.value;

    this.authService
      .createUserWithGoogle(this.cachedGoogleUser.idToken, undefined, companyName)
      .subscribe({
        next: () => {
          this.postLogin();
        },
        error: (err: HttpErrorResponse) => {
          // There are lots of kinds of error that could occur here, but they're all "our" fault, so don't surface them to the user
          this.error = 'Error creating account. Please try again later.';
          this.loading = false;
        },
      });
  }

  // createAccountGoogleForm submit action for Microsoft
  createAccountWithMicrosoft() {
    if (!this.cachedMicrosoftUser) {
      this.error = 'Oops, something went wrong. Please try again later.';
      this.authService.logOut();
      return;
    }

    if (!this.createAccountGoogleForm.valid) {
      this.error = 'Please fill out all fields to continue';
      return;
    }

    this.loading = true;

    const { companyName } = this.createAccountGoogleForm.value;

    this.authService
      .createUserWithMicrosoft(this.cachedMicrosoftUser.idToken, undefined, companyName)
      .subscribe({
        next: () => {
          this.postLogin();
        },
        error: (err: HttpErrorResponse) => {
          // There are lots of kinds of error that could occur here, but they're all "our" fault, so don't surface them to the user
          this.error = 'Error creating account. Please try again later.';
          this.loading = false;
        },
      });
  }

  // acceptInviteGoogleForm submit action
  acceptInviteWithGoogle() {
    if (!this.cachedGoogleUser) {
      this.error = 'Oops, something went wrong. Please try again later.';
      this.authService.logOut();
      return;
    }

    if (!this.inviteToken) {
      this.error = 'Oops, something went wrong. Please try again later.';
      this.authService.logOut();
      return;
    }

    if (!this.acceptInviteGoogleForm.valid) {
      this.error = 'Please fill out all fields to continue';
      return;
    }

    this.loading = true;

    this.authService
      .createUserWithGoogle(this.cachedGoogleUser.idToken, this.inviteToken, undefined)
      .subscribe({
        next: () => {
          this.postLogin();
        },
        error: (err: HttpErrorResponse) => {
          // There are lots of kinds of error that could occur here, but they're all "our" fault, so don't surface them to the user
          this.notify.error('Error creating account. Please try again later.');
          this.loading = false;
        },
      });
  }

  acceptInviteWithMicrosoft() {
    if (!this.cachedMicrosoftUser) {
      this.error = 'Oops, something went wrong. Please try again later.';
      this.authService.logOut();
      return;
    }

    if (!this.inviteToken) {
      this.error = 'Oops, something went wrong. Please try again later.';
      this.authService.logOut();
      return;
    }

    if (!this.acceptInviteGoogleForm.valid) {
      this.error = 'Please fill out all fields to continue';
      return;
    }

    this.loading = true;

    this.authService
      .createUserWithMicrosoft(this.cachedMicrosoftUser.idToken, this.inviteToken, undefined)
      .subscribe({
        next: () => {
          this.postLogin();
        },
        error: (err: HttpErrorResponse) => {
          // There are lots of kinds of error that could occur here, but they're all "our" fault, so don't surface them to the user
          this.notify.error('Error creating account. Please try again later.');
          this.loading = false;
        },
      });
  }

  // signupForm submit action
  signupWithEmail() {
    if (!this.signupForm.valid) {
      this.error = 'Please fill out all fields to continue';
      return;
    }

    this.loading = true;

    this.recaptcha.execute('create_account').subscribe({
      next: (token) => {
        let req: StartLedgerOnboardingRequest = {
          email: this.signupForm.value.email,
          name: this.signupForm.value.fullName,
          company: this.signupForm.value.companyName,
          recaptchaToken: token,
        };
        this.onboardingService.startWorkforceOnboarding(req).subscribe({
          next: () => {
            this.notify.success('Please click the link in your email to continue.');
            this.inviteSent = true;
            this.loading = false;
          },
          error: (err) => {
            if (err.status) {
              // These statuses from the service provide a useful error message we just pass through
              if (err.status === 409 || err.status === 400) {
                this.error = err.error.message;
                this.loading = false;
                return;
              }
            }
            this.error = 'Error sending invite. Please try again later. Code: lcu-400';
            this.loading = false;
          },
        });
      },
      error: () => {
        this.error = 'Error sending invite. Please try again later.';
      },
    });
  }

  // acceptInviteForm submit action
  acceptInviteWithEmail() {
    if (!this.acceptInviteForm.valid) {
      this.error = 'Please fill out all fields to continue';
      return;
    }

    if (!this.inviteToken || !this.invitation) {
      this.error = 'Oops, something went wrong. Please try again later.';
      return;
    }

    if (!this.checkPasswords(this.acceptInviteForm)) {
      this.error = 'Passwords do not match';
      return;
    }

    this.loading = true;

    const body: CreateUserRequest = {
      email: this.invitation.inviteeEmail,
      password: this.acceptInviteForm.value.password,
      name: this.acceptInviteForm.value.fullName,
      inviteToken: this.inviteToken,
    };

    this.createUser(body);
  }

  signInWithMicrosoft() {
    this.loading = true;
    this.socialAuthService.signIn(MicrosoftLoginProvider.PROVIDER_ID).then(
      (user) => {
        // Nothing to do - handle by the subscription
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        if (error && error.message && error.message.includes('user_cancelled')) {
          // User cancelled the sign-in, no need to show an error
          return;
        }
        this.notify.error('Error signing in with Microsoft. Please try again.');
      }
    );
  }

  // If someone already signed in with google but doesn't have an account yet, we need to cancel the signup process
  cancelCreateAccount() {
    this.cachedGoogleUser = null;
    this.cachedMicrosoftUser = null;
    this.authService.logOut();
  }

  checkPasswords(group: FormGroup): boolean {
    // Don't show an error if they haven't filled out the password field yet
    if (!group.get('password')?.valid) return true;
    if (!group.get('confirmPassword')?.value) return true;

    const pass = group.get('password')?.value;
    const confirmPass = group.get('confirmPassword')?.value;
    return pass === confirmPass;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  hasLength(pass: string) {
    return pass.length >= 8 && pass.length <= 64;
  }

  navigateToLogin() {
    this.isActionLogin = true;
  }

  navigateToSignup() {
    this.inviteError = false;
    this.isActionLogin = false;
  }

  async forgotPassword() {
    this.router.navigateByUrl('reset-password');
  }

  // loginForm submit action
  logIn() {
    this.error = undefined;

    if (!this.loginForm.valid) {
      this.error = 'Please fill out all fields to continue';
      this.loading = false;
      return;
    }

    this.loading = true;

    const { email, password } = this.loginForm.value;

    this.authService.loginV2(email.trim(), password).subscribe({
      next: () => {
        this.postLogin();
      },
      error: () => {
        this.loading = false;
        this.loginInvalid = true;
      },
    });
  }

  postLogin() {
    this.heapService.identify(this.authService.getUserValue.email);
    this.heapService.addUserProperties({
      is_employee: this.authService.email
        ? this.authService.email.endsWith('livedatatechnologies.com')
        : false,
    });
    window['Intercom']('boot', {
      app_id: environment.intercomAppId,
      email: this.authService.getUserValue.email,
      name: this.authService.getUserValue.name || '',
      user_id: this.authService.getUserValue.id,
      platform_mode: this.isWorkforceLogin ? 'workforce' : 'gotlivedata',
      companies: this.authService.getOrgsValue.map((o) => {
        return {
          company_id: o.id,
          created_at: o.createdAt,
          name: o.name,
        };
      }),
    });
    Sentry.setUser({
      email: this.authService.getUserValue.email,
      id: this.authService.getUserValue.id,
    });
    Hotjar.identify(this.authService.getUserValue.id, {
      is_employee: this.authService.email
        ? this.authService.email.endsWith('livedatatechnologies.com')
        : false,
      created_at: this.authService.getUserValue.createdAt,
      email: this.authService.getUserValue.email,
      source: this.authService.getUserValue.source || 'Unknown',
    });

    // If this is a dialog, close it after successful login
    if (this.isDialog && this.dialogRef) {
      this.dialogRef.close(true);
      return;
    }

    if (this.invitation && this.invitation.redirectUrl) {
      this.router.navigateByUrl(this.invitation.redirectUrl);
      return;
    }

    // Navigate based on redirect URL. If an invitation, we auto-accepted so don't send them there
    const useRedirectUrl = this.authService.$redirectUrl && !this.isInvite;
    const redirectUrl = useRedirectUrl ? this.authService.$redirectUrl : '/main';
    this.router.navigateByUrl(redirectUrl);

    this.loading = false;
  }

  // Close dialog method
  closeDialog() {
    if (this.isDialog && this.dialogRef) {
      this.dialogRef.close();
    }
  }

  private createUser(body: CreateUserRequest) {
    this.loading = true;

    try {
      this.recaptcha.execute('create_account').subscribe({
        next: (token) => {
          body.recaptchaToken = token;

          this.userService.createUser(body).subscribe({
            next: () => {
              this.notify.success(
                'User created successfully. Welcome to the Live Data revolution.'
              );
              this.loginForm.patchValue({
                email: body.email,
                password: body.password,
              });
              this.logIn();
            },
            error: (err: HttpErrorResponse) => {
              this.loading = false;

              if (err.status) {
                switch (err.status) {
                  case 409:
                    this.error =
                      'That email address is already taken. Perhaps you need to reset your password?';
                    break;
                  case 401:
                    this.error =
                      'Error creating user. Make sure your browser allows recaptcha and your invitation matches your email address.';
                    break;
                  case 400:
                    // Surface this to Sentry. Future improvement: https://app.clickup.com/t/8684v9uw7
                    if (err.message && err.message.includes('invitation token')) {
                      Sentry.captureException(err);
                    }
                    this.error =
                      'Sorry, there was a problem creating your account. Please try again later or contact support@livedatatechnologies.com for help. Code: lcu-400';
                    break;
                  case 500:
                    this.error =
                      'Sorry, there was a problem creating your account. Please try again later or contact support@livedatatechnologies.com for help. Code: lcu-500';
                    break;
                }
              } else {
                this.error =
                  'Sorry, there was a problem creating your account.  Please try again later or contact support@livedatatechnologies.com for help. Code: lcu-900';
              }
            },
          });
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.error = 'Security verification failed. Please try again.';
        },
      });
    } catch (e: any) {
      this.loading = false;

      if (e instanceof HttpErrorResponse) {
        const err = e as HttpErrorResponse;
        this.error = err.error;
      } else this.error = e.message;
    }
  }
}
